import type { TableMessage } from '../api/fetchers/tableLevelMessages/types';
import type { PrivateChatOptions, Message, PrivateMessageType } from '../store/slices/chat/types';
import { ChatBlockType, ChatRole, SendType, MessageType } from '../store/slices/chat/types';

export const LIVE_SUPPORT_WELCOME_MESSAGE: Message = {
  messageId: 'initial_support_chat_message',
  role: ChatRole.LiveSupport,
  tagMessage: 'SUPPORT',
  content: 'LIVE_WELCOME',
  userId: 'support',
  userScreenName: 'LIVE_SUPPORT',
  requireTranslation: true,
  timestamp: 0, // Display first
  showMessage: true,
  messageType: MessageType.SupportAgent,
};

export const isGameChatRestricted = (
  isBetRequiredToChat: boolean,
  isBetChatAllowed: boolean,
  isLowBalance: boolean,
  hasTotalBet: boolean
): boolean => {
  return isBetRequiredToChat ? !isBetChatAllowed : isLowBalance && !hasTotalBet;
};

export const generateMessage = (
  userId: string,
  input: string,
  tableId: string,
  casinoId: string,
  isGameChat: boolean = false
): Message => ({
  content: input.trim(),
  action: 'COMMAND',
  messageId: `${userId}_${Date.now()}`,
  userId,
  tableId,
  casinoId,
  role: ChatRole.GameUser,
  tagMessage: isGameChat ? 'GAME_USER' : 'SUPPORT',
  timestamp: Date.now(),
});

export const generateWelcomeMessage = (
  isCrashGame: boolean,
  tableName: string = '',
  dealerName: string = '',
  isPrivateTable: boolean = false
): TableMessage => {
  const chatMessageProps = isPrivateTable
    ? {
        role: ChatRole.Supervisor,
        content: 'WELCOME_TO_TABLE',
        timestamp: 0,
        messageType: MessageType.Host,
      }
    : {
        role: ChatRole.GameUser,
        content: `WELCOMETO ${tableName}`,
        timestamp: Date.now(),
        messageType: MessageType.GameUser,
      };

  return {
    messageId: 'dealer_welcome_message',
    tagMessage: 'GAME_USER',
    userId: 'dealer',
    userScreenName: isCrashGame ? undefined : dealerName, // Prevent dealer name from displaying in crash games
    requireTranslation: true,
    showMessage: true,
    hostManagerId: null,
    hostManagerName: null,
    messageRead: false,
    requestCreatedTimeStamp: null,
    requestStatus: null,
    requestType: null,
    sendToSelected: null,
    toCasinoId: null,
    toCasinoName: null,
    toTableId: null,
    toTableName: null,
    toUserId: null,
    toUserScreenName: null,
    ...chatMessageProps,
  };
};

export const parseSystemMessage = (text: string): string => {
  return text.replace(/<[^>]*>/g, '').trim();
};

// Rename showHide field to showMessage. Remove this after backend introduces showMessage field.
export const parseChatMessage = (message: TableMessage): TableMessage => {
  if (!('showMessage' in message)) {
    message.showMessage = message.showHide;
    delete message.showHide;
  }
  message.messageType = getMessageType(message);
  if (message.messageType === MessageType.AutomatedSupport) {
    message.requireTranslation = true;
  }

  return message;
};

export const createChatAvailableMessage = (betChatAllowed: boolean): Message => {
  const timestamp = Date.now();
  return {
    messageId: `chat_available_message_${timestamp}`,
    role: ChatRole.GameUser,
    tagMessage: 'GAME_USER',
    content: betChatAllowed ? 'CHAT_AVAILABLE' : 'CHAT_ACTIVATED',
    sendType: SendType.Notification,
    messageType: MessageType.Notification,
    requireTranslation: true,
    timestamp,
    showMessage: true,
  };
};

export const isBetRequiredToChat = (chatBlockType?: ChatBlockType): boolean =>
  chatBlockType === ChatBlockType.BetRequired;

export const getChatMessageType = (message: Message): MessageType => {
  if (message.tableId === ChatRole.Supervisor) {
    return MessageType.Host;
  }
  if (message.sendType === SendType.Notification) {
    return MessageType.Notification;
  }
  return MessageType.GameUser;
};

export const getSuportMessageType = (message: Message): MessageType => {
  if (message.automatedMessage) {
    return MessageType.AutomatedSupport;
  }
  if (
    message.role === ChatRole.GameUser &&
    !['FRESHCHAT_AGENT', 'FRESHCHAT_BOT'].includes(message.userScreenName ?? '')
  ) {
    return MessageType.SupportUser;
  }

  if (message.role === ChatRole.Support && message.sendType === SendType.Table) {
    return MessageType.System;
  }

  return MessageType.SupportAgent;
};

export const getMessageType = (message: Message): MessageType => {
  if (message.tagMessage === 'GAME_USER') {
    return getChatMessageType(message);
  }

  return getSuportMessageType(message);
};

export const getPrivateChatButtonsList = (privateChatOptions: PrivateChatOptions): PrivateMessageType[] => {
  const getTranslationKey = (key: string): string => {
    switch (key) {
      case 'REQUEST DEALER SESSION EXTENSION':
        return 'REQUEST_DEALER_EXTENSION';
      default:
        return key.replace(/\s(?!$)/g, '_').replace(/^\s/, '');
    }
  };

  return privateChatOptions
    ? Object.entries(privateChatOptions).map(([key, value]) => ({
        priveRequestType: key,
        name: getTranslationKey(key),
        message: value,
      }))
    : [];
};

const supportMessageTypes = new Set([MessageType.SupportAgent, MessageType.System, MessageType.AutomatedSupport]);
export const getIsSupportMessage = (messageType?: MessageType): boolean => {
  // Ignoring typescript to avoid Unnecessary ternary check, Set.has will handle undefined gracefully.
  return supportMessageTypes.has(messageType!);
};
